<i18n>
{
  "fr": {
    "question-count": "{answered}/{total, plural, 1 {1&nbsp;question} other {#&nbsp;questions}}",
    "step-count": "{completed}/{total}",
    "reset-statuses": "Réinitialiser les statuts complétés",
    "confirm-reset-title": "Réinitialiser les statuts complétés",
    "confirm-reset-message": "Cette action réinitialisera les statuts «\u00A0complétés\u00A0» de toute les étapes du questionnaire {lang, select, FRENCH {(en français seulement)} ENGLISH {(en anglais seulement)} other {}}. Aucune réponse ne sera affectée par cette opération. Souhaitez vous exécuter cette action?",
    "confirm-reset-confirm": "Réinitialiser",
    "confirm-reset-cancel":"Annuler",
    "export": "Exporter",
    "export-title": "Exporter le rapport ESG",
    "export-year": "Année du rapport",
    "cancel": "Annuler"
  },
  "en": {
    "question-count": "{answered}/{total, plural, 1 {1&nbsp;question} other {#&nbsp;questions}}",
    "step-count": "{completed}/{total}",
    "reset-statuses": "Reset completed statuses",
    "confirm-reset-title": "Reset completed statuses",
    "confirm-reset-message": "This will reset the \"completed\" statuses for all steps in the questionnaire {lang, select, FRENCH {(only in french)} ENGLISH {(only in english)} other {}}. No answer will be affected. Do you wish to do this?",
    "confirm-reset-confirm": "Reset",
    "confirm-reset-cancel":"Cancel",
    "export": "Export",
    "export-title": "Export ESG report",
    "export-year": "Report year",
    "cancel": "Cancel"
  }
}
</i18n>

<template>
  <ui-box
    class="questionnaire-sidebar"
    :class="{
      'py-24px': $media.desktop
    }"
  >
    <b-menu class="block" :activable="false" :accordion="false">
      <b-menu-list>
        <b-menu-item
          v-for="section in questionnaire.sections"
          :key="section.uniqueKey"
          :active="isSectionActive(section)"
          :expanded="isSectionActive(section)"
        >
          <template #label>
            <div class="columns is-mobile" style="align-items: center">
              <div class="column">
                <span class="has-text-weight-bold">{{ section.name }}</span>
              </div>
              <div
                class="column is-narrow"
                :class="getSectionIsCompleted(section) ? 'is-size-5' : 'is-size-7'"
                v-html="getSectionContentHtml(section)"
              ></div>
            </div>
          </template>

          <b-menu-item
            v-for="step in section.steps"
            :key="step.uniqueKey"
            :active="currentQuestionnaireStep === step"
            @click="$emit('update:currentQuestionnaireStep', step)"
          >
            <template #label>
              <div class="questionnaire-sidebar__step">
                <span class="is-block has-text-weight-semibold questionnaire-sidebar__step-title">{{ step.name }}</span>
                <span
                  class="is-narrow is-size-7"
                  :class="{ 'questionnaire-sidebar__step-icon--completed': step.isCompleted }"
                  v-html="getStepProgressHtml(step)"
                ></span>
              </div>
            </template>
          </b-menu-item>
        </b-menu-item>
      </b-menu-list>
    </b-menu>

    <div class="block">
      <div class="is-flex" style="justify-content: space-between; flex-wrap: wrap">
        <div>
          <b-button v-if="isAnyStepCompleted" @click="resetStatuses" size="is-small" class="m-1">
            {{ $t('reset-statuses') }}
          </b-button>
        </div>

        <div>
          <b-button @click="showExportModal = true" size="is-small" class="m-1">
            {{ $t('export') }}
          </b-button>

          <b-modal v-model="showExportModal" has-modal-card>
            <template #default="{ close }">
              <div class="modal-card">
                <header class="modal-card-head">
                  <h2 class="modal-card-title">{{ $t('export-title') }}</h2>
                  <button type="button" class="delete" @click="close"></button>
                </header>
                <section class="modal-card-body">
                  <s-field :label="$t('export-year')">
                    <b-select v-model="exportYear">
                      <option v-for="year in exportYearChoices" :key="year" :value="year">{{ year }}</option>
                    </b-select>
                  </s-field>
                </section>
                <footer class="modal-card-foot">
                  <b-button @click="exportPdf" :loading="exportLoading" type="is-primary">
                    {{ $t('export') }}
                  </b-button>
                  <b-button @click="close">
                    {{ $t('cancel') }}
                  </b-button>
                </footer>
              </div>
            </template>
          </b-modal>
        </div>
      </div>
    </div>
  </ui-box>
</template>
<script>
import range from 'lodash/range'

import BusinessService from '@/services/business'
import UiBox from '@/components/ui/box'
import SField from '@/components/form/field'

const NUM_YEARS = 20
const CURRENT_YEAR = new Date().getFullYear()

export default {
  components: {
    UiBox,
    SField
  },
  props: {
    questionnaire: Object,
    currentQuestionnaireLanguage: String,
    currentQuestionnaireStep: Object
  },
  data() {
    return {
      showExportModal: false,
      exportLoading: false,
      exportYear: CURRENT_YEAR
    }
  },
  computed: {
    isAnyStepCompleted() {
      return !!this.questionnaire.sections.flatMap((x) => x.steps).find((x) => x.isCompleted)
    },
    exportYearChoices() {
      return range(CURRENT_YEAR, CURRENT_YEAR - NUM_YEARS)
    }
  },
  methods: {
    isSectionActive(section) {
      return !!section.steps.find((x) => x === this.currentQuestionnaireStep)
    },
    getSectionIsCompleted(section) {
      const result = this.getSectionProgress(section)
      return result.isCompleted
    },
    getSectionContentHtml(section) {
      const result = this.getSectionProgress(section)
      return result.contentHtml
    },
    getSectionProgress(section) {
      const allSteps = section.steps
      const completedSteps = allSteps.filter((x) => x.isCompleted)

      const sectionResult = {
        contentHtml: this.$t('step-count', {
          completed: completedSteps.length,
          total: allSteps.length
        }),
        isCompleted: false
      }

      if (completedSteps.length === allSteps.length) {
        sectionResult.contentHtml = "<i class='fas fa-check-circle'></i>"
        sectionResult.isCompleted = true
      }

      return sectionResult
    },
    getStepProgressHtml(step) {
      if (step.isCompleted) {
        return "<i class='fas fa-check'></i>"
      }

      const allQuestions = step.groups.flatMap((x) => x.questions)
      const answeredQuestions = allQuestions.filter(({ answer }) => !!answer && answer.trim() !== '')

      return this.$t('question-count', {
        answered: answeredQuestions.length,
        total: allQuestions.length
      })
    },
    resetStatuses() {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-reset-title'),
        message: this.$t('confirm-reset-message', { lang: this.currentQuestionnaireLanguage }),
        cancelText: this.$t('confirm-reset-cancel'),
        confirmText: this.$t('confirm-reset-confirm'),
        type: 'is-danger',
        onConfirm: () => {
          this.$apollo.mutate({
            mutation: this.$options.mutation.ResetStatuses,
            variables: {
              businessId: BusinessService.getCurrentBusiness().id,
              language: this.currentQuestionnaireLanguage
            }
          })
        }
      })
    },
    async exportPdf() {
      this.exportLoading = true

      const { data } = await this.$apollo.mutate({
        mutation: this.$options.mutation.GeneratePdf,
        variables: {
          businessId: BusinessService.getCurrentBusiness().id,
          language: this.currentQuestionnaireLanguage,
          year: this.exportYear
        }
      })

      window.open(data.generateQuestionnairePdf.pdfUrl)

      this.exportLoading = false
      this.showExportModal = false
    }
  }
}
</script>
<graphql>
mutation ResetStatuses($businessId: ID!, $language: ContentLanguage!) {
  resetQuestionnaireStepStatuses(input: { businessId: $businessId, language: $language }) {
    questionnaire {
      id
      sections {
        id
        steps {
          id
          isCompleted(businessId: $businessId)
        }
      }
    }
  }
}

mutation GeneratePdf($businessId: ID!, $language: ContentLanguage!, $year: Int!) {
  generateQuestionnairePdf(input: { businessId: $businessId, language: $language, year: $year }) {
    pdfUrl
  }
}
</graphql>

<style lang="scss">
.questionnaire-sidebar {
  li ul {
    padding-left: 24px;
  }

  li a.is-active {
    background-color: $primary;
    border-radius: 4px;
  }

  li li a.is-active {
    background-color: transparent;
    color: $grey-dark;
  }

  li li a.is-active .questionnaire-sidebar__step-title {
    color: $primary;
    font-weight: 700 !important;
  }

  &__step {
    position: relative;
  }

  &__step-icon {
    &--completed {
      position: absolute;
      top: 2px;
      left: -24px;
    }
  }
}
</style>